import React, { useEffect, useState } from 'react';
import { IAuthorRelationshipStats } from '../../../core/modules/book/book.dto';
import ReloadIcon from '../../../assets/icons/reload.svg';
import TotalDealsIcon from '../../../assets/icons/stat-total-deals.svg';
import AcquiredDealsIcon from '../../../assets/icons/stat-acquired-deals.svg';
import LatestDealIcon from '../../../assets/icons/stat-latest-deal.svg';
import { formatDate, formatTimeAgo, pluralize } from './formatters';
import './author-relationship.css';

const CLOCK_TICK_MS = 30 * 1000;

interface IAuthorSyncStatusProps {
  fetchedAt: string;
  isRefreshing: boolean;
  onRefresh: () => void;
}

export const AuthorSyncStatus: React.FC<IAuthorSyncStatusProps> = ({
  fetchedAt,
  isRefreshing,
  onRefresh
}) => {
  const [now, setNow] = useState(Date.now());

  // Keep "Updated 4 min ago" honest while the overlay stays open.
  useEffect(() => {
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), CLOCK_TICK_MS);

    return () => clearInterval(timer);
  }, [fetchedAt]);

  return (
    <div className='ar-sync'>
      <span className='ar-sync-text'>Updated {formatTimeAgo(fetchedAt, now)} from HubSpot</span>
      <span className='ar-sync-dot'>·</span>
      <button
        type='button'
        className='ar-refresh'
        onClick={onRefresh}
        disabled={isRefreshing}
        aria-label='Refresh HubSpot data'
      >
        <img
          src={ReloadIcon}
          alt=''
          className={isRefreshing ? 'ar-refresh-icon ar-spin' : 'ar-refresh-icon'}
        />
        <span className='ar-refresh-label'>{isRefreshing ? 'Refreshing' : 'Refresh'}</span>
      </button>
    </div>
  );
};

interface IStatCardProps {
  label: string;
  icon: string;
  value: string | number;
  note: string;
  noteClassName?: string;
}

const StatCard: React.FC<IStatCardProps> = ({ label, icon, value, note, noteClassName }) => (
  <div className='ar-stat-card'>
    <div className='ar-stat-main'>
      <div className='ar-stat-label-row'>
        <span className='ar-stat-label'>{label}</span>
        <img src={icon} alt='' className='ar-stat-icon' />
      </div>
      <p className='ar-stat-value'>{value}</p>
    </div>
    <p className={`ar-stat-note ${noteClassName ?? ''}`}>{note}</p>
  </div>
);

interface IAuthorStatCardsProps {
  stats: IAuthorRelationshipStats;
}

const AuthorStatCards: React.FC<IAuthorStatCardsProps> = ({ stats }) => {
  const firstCreated = formatDate(stats.first_deal_created_at);

  return (
    <div className='ar-stat-cards'>
      <StatCard
        label='Total deals'
        icon={TotalDealsIcon}
        value={stats.total_deals}
        note={`${pluralize(stats.in_progress_deals, 'deal')} in progress`}
      />
      <StatCard
        label='Acquired deals'
        icon={AcquiredDealsIcon}
        value={stats.acquired_deals}
        note={`${pluralize(stats.lost_deals, 'lost deal')}`}
        noteClassName='ar-stat-note-warn'
      />
      <StatCard
        label='Latest deal created on'
        icon={LatestDealIcon}
        value={formatDate(stats.latest_deal_created_at) || '—'}
        note={firstCreated ? `First deal created on ${firstCreated}` : ''}
      />
    </div>
  );
};

export default AuthorStatCards;
